import { memoryStore, useStore } from "../../../lib/store";
import { useCallback, useEffect } from "react";
import { useCurrentUser } from "../../user/hooks/useCurrentUser";
import { DebugLogger } from "../../app/utils/debugLogger";
import { type Api, useApiQuery } from "../../../services/api";
import type { RoomDiscoveryChannel } from "../../../tools/analytics/domains/invite.analytics";
import {
  clearRoomInviteCodeData,
  hasPendingRoomInvite,
  roomInviteCodeStore,
  TWO_WEEKS_IN_MS,
} from "../stores/roomInviteCodeStore";

const debug = new DebugLogger("useSignupOnboardingState");
debug.mute();

/** Minimal translatable item with id. Used for profession/specialty in onboarding state. */
type TranslatableWithId = { id: string; uid: string };

export type OnboardingTag =
  Api["get"]["/webapp/public/tags/parents"]["output"]["body"][number];

export type OnboardingProfession =
  Api["get"]["/webapp/public/professions"]["output"]["body"][number];

export type InvitePreviewRoom =
  Api["get"]["/webapp/public/rooms/by-invite/:code"]["output"]["body"];

interface StateModel {
  profession: TranslatableWithId | null;
  mainSpecialty: TranslatableWithId | null;
  otherSpecialties: OnboardingTag[];
  skippedProfession: boolean;
  skippedSpecialty: boolean;
  roomInviteCode: string | null;
  roomForceProfession: string | null;
  roomForceSpecialties: string[];
  roomUtmSource: string | null;
  roomUtmMedium: string | null;
  roomUtmContent: string | null;
  roomDiscoveryChannel: RoomDiscoveryChannel | null;
  roomPlaylistId: string | null;
  firebaseMessageTokenAsked: boolean;
}

const initialSignupOnboardingState: StateModel = {
  profession: null,
  mainSpecialty: null,
  otherSpecialties: [],
  skippedProfession: false,
  skippedSpecialty: false,
  roomInviteCode: null,
  roomForceProfession: null,
  roomForceSpecialties: [],
  roomUtmSource: null,
  roomUtmMedium: null,
  roomUtmContent: null,
  roomDiscoveryChannel: null,
  roomPlaylistId: null,
  firebaseMessageTokenAsked: false,
};

const signupOnboardingStateStore = memoryStore<StateModel>(
  initialSignupOnboardingState
);

export function useSignupOnboardingState(): {
  onboardingRoomInviteCode: string | null;
  onboardingRoomForceProfession: string | null;
  onboardingRoomForceSpecialties: string[];
  onboardingRoomData: InvitePreviewRoom | null;
  onboardingUserProfession: TranslatableWithId | null;
  onboardingUserMainSpecialty: TranslatableWithId | null;
  onboardingUserOtherSpecialties: OnboardingTag[];
  onboardingRoomUtmSource: string | null;
  onboardingRoomUtmMedium: string | null;
  onboardingRoomUtmContent: string | null;
  onboardingRoomDiscoveryChannel: RoomDiscoveryChannel | null;
  onboardingRoomPlaylistId: string | null;
  isInvitedToRoom: boolean;
  isRoomDataMissing: boolean;
  skippedProfession: boolean;
  skippedSpecialty: boolean;
  firebaseMessageTokenAsked: boolean;
  updateSignupOnboardingState: (newState: Partial<StateModel>) => void;
  clearSignupOnboardingState: () => void;
} {
  const [onboardingState, setOnboardingState] = useStore(
    signupOnboardingStateStore
  );
  const { currentUser } = useCurrentUser();

  // The pending invite, resolved from the in-memory state OR the durable store.
  // Account creation on the acquisition slide remounts this flow under the new
  // Firebase auth, and the in-memory `roomInviteCode` can be null before the
  // hydration effect re-runs — which dropped invited users onto the B2C exit and,
  // for OAuth-gated rooms, would have skipped SSO (joinRoom saw an undefined
  // `oauthRoomConfig`). localStorage survives the remount, so deriving from it
  // keeps the whole invite context — room data → `oauthRoomConfig`, `isInvitedToRoom`,
  // the finalize join — correct throughout. TTL-checked, mirroring the hydration guard.
  const durablePending = hasPendingRoomInvite()
    ? roomInviteCodeStore.get()
    : null;
  const effectiveInviteCode =
    onboardingState.roomInviteCode ??
    (durablePending !== null ? durablePending.roomInviteCode : null);

  const updateSignupOnboardingState = useCallback(
    (newState: Partial<StateModel>) => {
      debug.log("Updating onboarding state...", newState);
      setOnboardingState((prevState) => ({ ...prevState, ...newState }));
    },
    [setOnboardingState]
  );

  const clearSignupOnboardingState = useCallback(() => {
    debug.log("Clearing onboarding state...");
    setOnboardingState(initialSignupOnboardingState);
  }, [setOnboardingState]);

  // Hydrate state from localStorage on mount (invite-code payload persists across
  // reload while the user completes onboarding).
  useEffect(() => {
    const stored = roomInviteCodeStore.get();
    if (stored === null) return;

    if (Date.now() > stored.timestamp + TWO_WEEKS_IN_MS) {
      console.warn("Expired roomInviteCodeData in localStorage, clearing");
      clearRoomInviteCodeData();
      return;
    }

    setOnboardingState((s) => ({
      ...s,
      roomInviteCode: stored.roomInviteCode,
      roomForceProfession: stored.roomForceProfession ?? null,
      roomForceSpecialties: stored.roomForceSpecialties ?? [],
      roomUtmSource: stored.roomUtmSource ?? null,
      roomUtmMedium: stored.roomUtmMedium ?? null,
      roomUtmContent: stored.roomUtmContent ?? null,
      roomDiscoveryChannel: stored.roomDiscoveryChannel ?? null,
      roomPlaylistId: stored.roomPlaylistId ?? null,
    }));
  }, [setOnboardingState]);

  // Fetch room data when invite code is available. Derived in render — not stored.
  const roomQuery = useApiQuery(
    "/webapp/public/rooms/by-invite/:code",
    effectiveInviteCode !== null
      ? { param: { code: effectiveInviteCode } }
      : false
  );
  const onboardingRoomData =
    roomQuery.state === "success" ? roomQuery.body : null;

  // Drop a pending invite ONLY when the server says the code resolves to no room
  // (404). A transient failure is not a verdict on the invite and must never
  // discard it: the code stays valid and a refetch resolves it. Clearing on any
  // error conflated "invalid invite" with "request cancelled by the
  // account-creation remount" / a network blip / a 5xx, which dropped valid
  // invites and sent invited users to the B2C exit instead of their room. (An
  // abort no longer even reaches `error` — see createQueryResult — but the
  // 404-only guard is the correctness invariant regardless.)
  const roomInviteNotFound =
    roomQuery.state === "error" && roomQuery.status === 404;
  useEffect(() => {
    if (roomInviteNotFound && effectiveInviteCode !== null) {
      console.error("Invite code resolves to no room; clearing stale invite.");
      clearRoomInviteCodeData();
      setOnboardingState((prev) => ({ ...prev, roomInviteCode: null }));
    }
  }, [roomInviteNotFound, effectiveInviteCode, setOnboardingState]);

  const isInvitedToRoom = effectiveInviteCode !== null;
  const isRoomDataMissing = isInvitedToRoom && onboardingRoomData === null;

  // Coalesce selection-in-progress with currently-saved server state.
  // Store wins (user made a fresh selection); currentUser is the fallback
  // (returning user resumes with their existing profile).
  const onboardingUserProfession =
    onboardingState.profession ?? currentUser?.professionModel ?? null;
  const onboardingUserMainSpecialty =
    onboardingState.mainSpecialty ?? currentUser?.mainSpecialtyModel ?? null;

  return {
    onboardingRoomInviteCode: effectiveInviteCode,
    onboardingRoomForceProfession: onboardingState.roomForceProfession,
    onboardingRoomForceSpecialties: onboardingState.roomForceSpecialties,
    onboardingRoomData,
    onboardingUserProfession,
    onboardingUserMainSpecialty,
    onboardingUserOtherSpecialties: onboardingState.otherSpecialties,
    onboardingRoomUtmSource: onboardingState.roomUtmSource,
    onboardingRoomUtmMedium: onboardingState.roomUtmMedium,
    onboardingRoomUtmContent: onboardingState.roomUtmContent,
    onboardingRoomDiscoveryChannel: onboardingState.roomDiscoveryChannel,
    onboardingRoomPlaylistId: onboardingState.roomPlaylistId,
    isInvitedToRoom,
    isRoomDataMissing,
    skippedProfession: onboardingState.skippedProfession,
    skippedSpecialty: onboardingState.skippedSpecialty,
    firebaseMessageTokenAsked: onboardingState.firebaseMessageTokenAsked,
    updateSignupOnboardingState,
    clearSignupOnboardingState,
  };
}
